import { Link } from 'react-router-dom';
import { LegalLayout } from '../components/LegalLayout';
import '../legal.css';

interface Subprocessor {
  name: string;
  jurisdiction: string;
  purpose: string;
  data: string;
  transfer: string;
  default: boolean;
}

const CORE_SUBPROCESSORS: Subprocessor[] = [
  {
    name: 'Oracle Cloud Infrastructure',
    jurisdiction: 'United States — Arizona (us-phoenix-1, the only Rynora SaaS region live today).',
    purpose: 'Primary compute + Postgres + container hosting for the Rynora control plane and Playwright runners.',
    data: 'All customer data at rest (Postgres tables, container filesystem). Single-cluster v0.',
    transfer:
      'EU SCCs + UK IDTA cover the cross-border transfer for EU / UK customers until the EU-Frankfurt region reaches general availability — see /trust/regions for the rolling status.',
    default: true,
  },
  {
    name: 'Backblaze B2',
    jurisdiction: 'United States — US-West (s3.us-west-002.backblazeb2.com).',
    purpose: 'Object storage for run artefacts: screenshots, videos, DOM traces, visual-diff baselines.',
    data: 'Run artefacts. Encrypted client-side before upload (the bucket itself only sees ciphertext).',
    transfer:
      'EU SCCs + UK IDTA cover EU / UK customer artefact uploads until an EU bucket is wired to the EU-Frankfurt region.',
    default: true,
  },
  {
    name: 'Cloudflare',
    jurisdiction: 'United States; EU traffic served from EU PoPs',
    purpose: 'DNS, TLS termination, WAF, bot mitigation, inbound email routing.',
    data: 'Request metadata (URLs, headers, IPs). Bodies are not stored beyond log retention windows. Hashed IP with per-instance salt.',
    transfer: 'EU SCCs (Cloudflare DPA) for EU personal data.',
    default: true,
  },
  {
    name: 'Resend',
    jurisdiction: 'United States',
    purpose: 'Outbound transactional email (account verification, password reset, run-finished notifications).',
    data: 'Recipient email + email body. Retained 30 days by Resend per their default policy.',
    transfer: 'EU SCCs (Resend DPA).',
    default: true,
  },
  {
    name: 'Stripe',
    jurisdiction: 'United States (Stripe Inc.); Ireland (Stripe Payments Europe Ltd.) for EU orgs',
    purpose: 'Billing and subscription payment processing.',
    data: 'Customer ID, plan, last-4 of card. Full card data never reaches Rynora.',
    transfer: 'Stripe DPA + SCCs. PCI DSS Level 1 attested.',
    default: true,
  },
  {
    name: 'Sentry',
    jurisdiction: 'United States (Sentry self-hosted EU option available on request)',
    purpose: 'Backend and frontend error tracking.',
    data: 'Stack traces, request URL, sanitised request body, user ID. PII scrubbed by allowlist before send.',
    transfer: 'Sentry DPA + EU SCCs.',
    default: true,
  },
  {
    name: 'Better Stack',
    jurisdiction: 'European Union (Czech Republic)',
    purpose: 'Uptime monitoring and public status-page hosting.',
    data: 'Endpoint URLs + response codes + latency. No customer data.',
    transfer: 'EU-based processor, no transfer required for EU orgs.',
    default: true,
  },
  {
    name: 'Grafana Cloud',
    jurisdiction: 'United States; European Union (Grafana Cloud Frankfurt) on request',
    purpose: 'Metrics + alerts (mirrored to operator Discord channel).',
    data: 'Operational metrics (latency, error rate, queue depth). No customer-payload data.',
    transfer: 'Grafana DPA + SCCs.',
    default: true,
  },
  {
    name: 'GitHub',
    jurisdiction: 'United States (subsidiary of Microsoft Corp.)',
    purpose: 'Source code hosting + CI (GitHub Actions). Customer data is not processed here.',
    data: 'No customer data. Only Rynora source code and CI logs.',
    transfer: 'Microsoft DPA + EU SCCs.',
    default: true,
  },
];

const AI_SUBPROCESSORS: Subprocessor[] = [
  {
    name: 'OpenAI (API platform)',
    jurisdiction: 'United States',
    purpose:
      'Default LLM provider for intent parsing, healing explanations, and conversational debug. Used only when AI features are enabled by the org admin.',
    data: 'Test-flow text content + recent run-error excerpts that the relevant AI feature was invoked on. Zero-retention enterprise mode used by default.',
    transfer:
      'OpenAI Enterprise DPA + EU SCCs. Zero-retention API mode; no training on submitted data per OpenAI policy.',
    default: false,
  },
  {
    name: 'Anthropic (Claude API)',
    jurisdiction: 'United States',
    purpose: 'Optional LLM provider for heavier reasoning paths (root-cause analysis, multi-step debug).',
    data: 'Same scope as OpenAI when this feature is routed to Anthropic.',
    transfer: 'Anthropic DPA + EU SCCs. Zero-retention API mode; no training on submitted data per Anthropic policy.',
    default: false,
  },
  {
    name: 'Groq',
    jurisdiction: 'United States',
    purpose: 'Optional low-latency LLM provider for inline UX (suggestions, autocomplete-style features).',
    data: 'Same scope as OpenAI when this feature is routed to Groq.',
    transfer: 'Groq DPA + EU SCCs. No training on submitted data per Groq policy.',
    default: false,
  },
];

export function SubprocessorsPage() {
  return (
    <LegalLayout
      title="Sub-processors"
      updated="June 1, 2026"
      summary="Every third party that processes customer data on Rynora's behalf, with jurisdiction, purpose, data categories, and transfer mechanism. Material additions are announced 30 days in advance via the subscription below."
      needsReview={false}
    >
      <div className="legal-banner legal-subprocessor-banner" role="note">
        <div>
          <strong>Region reality check.</strong> Every Rynora SaaS org runs in the single <code>us-phx</code> region
          today. EU-FRA is in design-partner alpha; AP-SYD is on the public roadmap.{' '}
          <Link to="/trust/regions">See /trust/regions</Link> for the current status of each region, plus the
          sub-processor footprint changes that will land when EU-FRA is generally available. The table below describes
          what we actually run today — not what we plan to run.
        </div>
      </div>

      <h2>What a sub-processor is</h2>
      <p>
        A <em>sub-processor</em> is a third-party service that Rynora uses to deliver the platform, and that has access
        to customer data (even if only transiently). This page lists every active sub-processor and our commitments
        about how they are managed. It is the canonical list referenced by Rynora's Data Processing Agreement.
      </p>
      <p>
        EU and UK customers who require an in-region option can register interest in the EU-FRA design-partner programme
        by emailing <a href="mailto:enterprise@rynora.app">enterprise@rynora.app</a>. The EU-FRA region will ship a
        parallel set of EU sub-processors (Sentry EU instance, Grafana EU instance, Resend EU pool, Cloudflare EU PoPs
        only) so that no customer data leaves the EU once the region is generally available. That sub-processor split is
        documented in advance on <Link to="/trust/regions">/trust/regions</Link>.
      </p>

      <h2>Subscribe to change notifications</h2>
      <p>
        We email a 30-day advance notice before any material addition or change to this list. Subscribe by sending an
        email with subject line <code>SUBSCRIBE</code> to{' '}
        <a href="mailto:trust@rynora.app?subject=SUBSCRIBE">trust@rynora.app</a>. We do not use the address for anything
        else; unsubscribe by replying <code>UNSUBSCRIBE</code>.
      </p>

      <h2>Core sub-processors</h2>
      <p>Required to deliver the platform. Every customer on every plan touches these.</p>
      <table>
        <thead>
          <tr>
            <th>Sub-processor</th>
            <th>Jurisdiction</th>
            <th>Purpose</th>
            <th>Data categories</th>
            <th>Transfer mechanism</th>
          </tr>
        </thead>
        <tbody>
          {CORE_SUBPROCESSORS.map((sp) => (
            <tr key={sp.name}>
              <td>
                <strong>{sp.name}</strong>
              </td>
              <td>{sp.jurisdiction}</td>
              <td>{sp.purpose}</td>
              <td>{sp.data}</td>
              <td>{sp.transfer}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h2>AI / LLM sub-processors (opt-in)</h2>
      <p>
        Used only when an org admin enables AI features. Disabled by default for new self-hosted orgs and on the{' '}
        <Link to="/sovereign-ai">sovereign tier</Link>; on the SaaS tier, an org admin enables them at org-creation
        time. The org admin can flip individual features back off (or switch them to a different provider) at any time
        via Admin Settings &rarr; AI providers.
      </p>
      <p>
        For buyers whose compliance posture forbids any commercial LLM provider entirely, the{' '}
        <Link to="/sovereign-ai">sovereign tier</Link> replaces every row in this table with a self-hosted Ollama / vLLM
        instance running inside the buyer's perimeter. None of the providers below are then in scope.
      </p>
      <table>
        <thead>
          <tr>
            <th>Provider</th>
            <th>Jurisdiction</th>
            <th>Purpose</th>
            <th>Data categories</th>
            <th>Transfer mechanism</th>
          </tr>
        </thead>
        <tbody>
          {AI_SUBPROCESSORS.map((sp) => (
            <tr key={sp.name}>
              <td>
                <strong>{sp.name}</strong>
              </td>
              <td>{sp.jurisdiction}</td>
              <td>{sp.purpose}</td>
              <td>{sp.data}</td>
              <td>{sp.transfer}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p>
        See the <Link to="/trust/ai">AI Bill of Materials</Link> for a per-feature mapping of which provider serves
        which Rynora surface and which model is used by default.
      </p>

      <h2>Sub-processor change policy</h2>
      <ul>
        <li>
          <strong>Material additions</strong> (a new vendor that processes customer data): 30 days advance notice via
          the email subscription above, with the right for the customer to object before the change takes effect.
        </li>
        <li>
          <strong>Material changes</strong> (existing vendor changes data category, jurisdiction, or sub-purpose): same
          30-day notice.
        </li>
        <li>
          <strong>Vendor sub-jurisdictional changes</strong> (e.g. a vendor opens a new region we do not yet use): no
          notice required; we update this page on the next quarterly review.
        </li>
        <li>
          <strong>Removals</strong>: no notice required; we update this page on the next quarterly review.
        </li>
      </ul>

      <h2>What this page does not cover</h2>
      <ul>
        <li>
          <strong>Customer-elected integrations</strong> (Jira, Slack, GitHub, GitLab, Jenkins, Postman, etc.) — the
          customer chooses the integration in-product; the integrated vendor is then the customer's own sub-processor,
          not Rynora's.
        </li>
        <li>
          <strong>BYO-LLM providers</strong> — when an org admin configures their own OpenAI / Anthropic / Azure OpenAI
          key under Admin Settings &rarr; AI providers, that provider is the customer's own sub-processor.
        </li>
        <li>
          <strong>Self-hosted and sovereign deployments</strong> — when Rynora runs entirely inside the customer's
          perimeter (see <Link to="/sovereign-ai">/sovereign-ai</Link>), no sub-processor on this page processes the
          customer's data. The customer manages their own infrastructure providers.
        </li>
      </ul>

      <h2>Questions</h2>
      <p>
        For procurement questions about any vendor on this page, or to request a copy of the underlying DPA between
        Rynora and that vendor, email <a href="mailto:trust@rynora.app">trust@rynora.app</a>.
      </p>
    </LegalLayout>
  );
}
